import React, { useState, useEffect, useCallback } from 'react';
import { useAppDispatch } from '../../store';
import { useTranslation } from 'react-i18next';
import apis from '../../utils/apis';
import { addResourceToProject, checkResourceAccess } from '../../store/actions/Projects/ProjectActions';
import { showModal, hideModal } from '../../store/actions/modalActions';
import Button from '../shared/TailwindComponents/Button';
import Badge from '../shared/TailwindComponents/Badge';
import { BadgeColor } from '../shared/TailwindComponents/types/badgeTypes';
import AccessReviewDialog from './AccessReviewDialog';
import {
  PiBriefcaseLight,
  PiCheckSquareOffsetLight,
  PiTableLight,
  PiFileMagnifyingGlassLight,
  PiBookOpenTextLight,
  PiDatabaseLight,
  PiFileTextLight,
  PiCheckCircleLight,
  PiLinkLight,
  PiChatCircleTextLight,
} from 'react-icons/pi';
import debounce from 'lodash.debounce';
import SearchInput from '../shared/TailwindComponents/SearchInput';
import Spinner from '../shared/TailwindComponents/Spinner';

// ─── Resource types ─────────────────────────────────────────────

/**
 * OWNED vs SHARED - the distinction this panel used not to make.
 *
 * A matter, a task, a table, a review, a draft or a research session is a
 * piece of WORK: it belongs to the project it is linked to, and linking it
 * says so. A template, a data source and a playbook are LIBRARY resources —
 * an org writes one contract template and every project uses it. Presenting
 * those as a link ("this template now belongs to this project") is a lie
 * about the model, and the panel used to treat all seven identically.
 *
 * So only owned resources are linkable here. The shared three are named below
 * the pills as what they are: available to every project, scoped to none.
 *
 * ⚠ PARKED (backend): making the shared three attachable to MANY projects at
 * once needs a non-exclusive join table, which the user has deferred. When it
 * lands they become their own group here - not more pills.
 */
const RESOURCE_TYPES: { key: string; label: string; icon: React.ReactElement; color: BadgeColor; endpoint: string }[] = [
  { key: 'matter', label: 'Matters', icon: <PiBriefcaseLight className="w-4 h-4" />, color: 'blue', endpoint: '/matters/view' },
  { key: 'task', label: 'Tasks', icon: <PiCheckSquareOffsetLight className="w-4 h-4" />, color: 'green', endpoint: '/tasks/list' },
  { key: 'table', label: 'Tables', icon: <PiTableLight className="w-4 h-4" />, color: 'teal', endpoint: '/tables' },
  { key: 'review_session', label: 'Reviews', icon: <PiFileMagnifyingGlassLight className="w-4 h-4" />, color: 'sky', endpoint: '/reviews' },
  // Added here: both were linkable work with nowhere to link them from.
  { key: 'draft', label: 'Drafts', icon: <PiFileTextLight className="w-4 h-4" />, color: 'purple', endpoint: '/drafting/drafts' },
  { key: 'research', label: 'Research', icon: <PiChatCircleTextLight className="w-4 h-4" />, color: 'indigo', endpoint: 'research/chat-groups' },
];

/** Library resources: available to every project, owned by none. */
/**
 * The singular noun for a resource type, TRANSLATED, ready to be dropped into
 * a sentence.
 *
 * This used to be `label.replace(/s$/, '').toLowerCase()` - English plural
 * rules applied to an English label, with the result interpolated into a
 * sentence that `t()` had already translated. In Bulgarian that produced
 * "Този matter остава частен по подразбиране": a Bulgarian sentence with an
 * English word wedged into it, and the word in the wrong case besides.
 *
 * A per-type key is the only thing that works. The derivation cannot be
 * fixed - no amount of suffix-stripping makes an English plural into a
 * Bulgarian singular - so the noun is a translated string in its own right,
 * and the fallback is the type key rather than a mangled label.
 */
const RESOURCE_SINGULAR: Record<string, string> = {
  matter: 'matter',
  task: 'task',
  table: 'table',
  review_session: 'review',
  draft: 'draft',
  research: 'research session',
  template: 'template',
  datasource: 'data source',
  playbook: 'playbook',
};

export const resourceSingular = (
  key: string,
  fallbackLabel?: string
): string => RESOURCE_SINGULAR[key] || fallbackLabel?.replace(/s$/, '').toLowerCase() || key;

const SHARED_TYPES: { key: string; label: string; icon: React.ReactElement }[] = [
  { key: 'template', label: 'Templates', icon: <PiFileTextLight className="w-3.5 h-3.5" /> },
  { key: 'datasource', label: 'Data Sources', icon: <PiDatabaseLight className="w-3.5 h-3.5" /> },
  { key: 'playbook', label: 'Playbooks', icon: <PiBookOpenTextLight className="w-3.5 h-3.5" /> },
];

/**
 * A resource's status, coloured. Every type spells its own - a matter is
 * `open`, a review is `completed`, a draft is `not_generated` - so the map is
 * by MEANING, and anything unrecognised stays neutral rather than guessing.
 */
const STATUS_COLORS: Record<string, BadgeColor> = {
  open: 'green', active: 'green', in_progress: 'blue', ongoing: 'blue',
  pending: 'yellow', queued: 'yellow', draft: 'gray', not_generated: 'gray',
  processing: 'blue', generating: 'blue',
  completed: 'green', done: 'green', closed: 'gray', archived: 'gray',
  failed: 'red', error: 'red', cancelled: 'gray',
};

const statusColor = (status: string): BadgeColor =>
  STATUS_COLORS[String(status).toLowerCase()] || 'gray';

/** `not_generated` → `Not generated`, so a raw enum never reaches a badge. */
const titleCaseStatus = (status: string): string => {
  const spaced = String(status).replace(/[_-]+/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

interface ProjectResourcesPanelProps {
  projectUuid: string;
  onClose: () => void;
  onResourceLinked?: () => void;
}

const ProjectResourcesPanel: React.FC<ProjectResourcesPanelProps> = ({ projectUuid, onClose, onResourceLinked }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [selectedType, setSelectedType] = useState<string>('matter');
  const [searchTerm, setSearchTerm] = useState('');
  // Immediate field value; `searchTerm` stays the debounced value the query uses.
  const [searchInput, setSearchInput] = useState('');
  const [resources, setResources] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [linkingId, setLinkingId] = useState<string | null>(null);
  const [linkedIds, setLinkedIds] = useState<Set<string>>(new Set());

  // Fetch already-linked resource UUIDs on mount
  useEffect(() => {
    const fetchLinked = async () => {
      try {
        const response = await apis.get(`/projects/${projectUuid}/resources`);
        const data = response.data;
        const uuids = new Set<string>();
        // Use the flat linked_resource_uuids list (includes ALL linked UUIDs
        // regardless of the caller's access level) so the link panel correctly
        // shows "Linked" for every already-linked resource.
        if (data?.linked_resource_uuids) {
          for (const uuid of data.linked_resource_uuids) {
            uuids.add(uuid);
          }
        } else if (data?.resources) {
          // Fallback for older backend versions
          for (const items of Object.values(data.resources) as any[][]) {
            for (const item of items) {
              if (item.resource_uuid) uuids.add(item.resource_uuid);
            }
          }
        }
        setLinkedIds(uuids);
      } catch (error) {
        // Non-critical - panel still works, just won't show pre-linked state
      }
    };
    fetchLinked();
  }, [projectUuid]);

  // Fetch resources when type or search changes.
  // Uses AbortController to cancel stale requests - when the user toggles
  // tabs quickly, an older response can resolve after a newer one and
  // overwrite the list with the wrong resource type.
  useEffect(() => {
    const abortController = new AbortController();

    const fetchResources = async () => {
      const typeConfig = RESOURCE_TYPES.find((t) => t.key === selectedType);
      if (!typeConfig) return;

      setIsLoading(true);
      try {
        const params: any = { page: 1, page_size: 50 };
        if (searchTerm) params.search_term = searchTerm;

        const response = await apis.get(typeConfig.endpoint, {
          params,
          signal: abortController.signal,
        });

        // If this request was aborted while in flight, don't update state
        if (abortController.signal.aborted) return;

        let items: any[] = [];
        const data = response.data;
        if (data.items) items = data.items;
        else if (data.matters) items = data.matters;
        else if (data.tasks) items = data.tasks;
        else if (data.tables) items = data.tables;
        else if (data.reviews) items = data.reviews;
        else if (data.playbooks) items = data.playbooks;
        else if (data.datasources) items = data.datasources;
        else if (data.templates) items = data.templates;
        // `/drafting/drafts` answers `{ data: [...] }`.
        else if (Array.isArray(data)) items = data;
        else if (data.data && Array.isArray(data.data)) items = data.data;

        setResources(items);
      } catch (error: any) {
        // Aborted requests throw - don't clear the list for those
        if (error?.name === 'CanceledError' || abortController.signal.aborted) return;
        setResources([]);
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    fetchResources();

    // Cleanup: abort the in-flight request when selectedType or searchTerm changes
    return () => abortController.abort();
  }, [selectedType, searchTerm]);

  const handleLink = async (resourceUuid: string) => {
    setLinkingId(resourceUuid);
    const result = await dispatch(addResourceToProject(projectUuid, selectedType, resourceUuid));
    setLinkingId(null);
    if (!result) return;

    setLinkedIds((prev) => new Set(prev).add(resourceUuid));
    if (onResourceLinked) onResourceLinked();

    // Backend now returns `missing_access_users` directly in the link response
    // (see project_service.add_resource). If the list is non-empty, surface
    // the access-review dialog so the linker sees the asymmetric consequence
    // of their action - matches the "attach \u2260 share" legal default from
    // sharded-frolicking-lake.md.
    const missingUsers: any[] = Array.isArray(result.missing_access_users) ? result.missing_access_users : [];
    if (missingUsers.length === 0) return;

    // The dialog still calls /access-check for `can_grant_access`, but we pass
    // the user list from the link response for immediate rendering. The
    // separate checkResourceAccess call remains for the authoritative
    // can_grant_access flag (admin vs non-admin linker).
    const accessResult = await dispatch(checkResourceAccess(projectUuid, selectedType, resourceUuid));
    if (!accessResult || !accessResult.collaborators?.length) return;

    const typeConfig = RESOURCE_TYPES.find((t) => t.key === selectedType);
    dispatch(showModal({
      showModal: true,
      type: 'custom',
      message: t('Review access'),
      width: '480px',
      content: (
        <AccessReviewDialog
          projectUuid={projectUuid}
          resourceType={selectedType}
          resourceUuid={resourceUuid}
          resourceLabel={t(resourceSingular(selectedType, typeConfig?.label))}
          collaborators={accessResult.collaborators}
          canGrantAccess={accessResult.can_grant_access ?? false}
          onClose={() => dispatch(hideModal())}
          onAccessGranted={() => { if (onResourceLinked) onResourceLinked(); }}
        />
      ),
      onCancel: () => {},
    }));
  };

  const debouncedSearch = useCallback(
    debounce((term: string) => {
      setSearchTerm(term);
    }, 300),
    []
  );

  const getResourceTitle = (resource: any) =>
    resource.title ||
    resource.name ||
    resource.label ||
    // Templates list uses PascalCase: TemplateName / Structure.title.
    resource.TemplateName ||
    resource?.Structure?.title ||
    resource.original_filename ||
    'Untitled';
  const getResourceUuid = (resource: any) => resource.uuid || resource.id;
  const getResourceStatus = (resource: any) => resource.status || resource.processing_status;

  const activeTypeConfig = RESOURCE_TYPES.find((t) => t.key === selectedType);

  return (
    <div className="flex flex-col h-full">
      {/* Type selector pills */}
      <div className="px-5 py-4 border-b border-hairline">
        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-3 tracking-wide">{t('Resource type')}</p>
        <div className="flex flex-wrap gap-1.5">
          {RESOURCE_TYPES.map((type) => (
            <button
              key={type.key}
              onClick={() => {
                setSelectedType(type.key);
                setSearchTerm('');
                setResources([]);
              }}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-md transition-all ${
                selectedType === type.key
                  ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 font-medium ring-1 ring-primary-200 dark:ring-primary-700'
                  : 'bg-surface-sunken text-fg-muted hover:bg-surface-soft'
              }`}
            >
              {type.icon}
              {t(type.label)}
            </button>
          ))}
        </div>

        {/* The library resources, named as what they are rather than offered
            as a link this project would own. See RESOURCE_TYPES. */}
        <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-fg-muted">
          {SHARED_TYPES.map((type) => (
            <span key={type.key} className="inline-flex items-center gap-1">
              {type.icon}
              {t(type.label)}
            </span>
          ))}
          <span>{t('are shared across your organisation — available to every project, scoped to none.')}</span>
        </div>
      </div>

      {/* Search */}
      <div className="px-5 py-3 border-b border-hairline-soft">
        <SearchInput
          value={searchInput}
          onChange={(value) => {
            setSearchInput(value);
            debouncedSearch(value);
          }}
          placeholder={t('Search {{type}}...', { type: activeTypeConfig?.label || '' })}
        />
      </div>

      {/*
        RESOURCE TABLE.

        This was a stacked list - a title on one line, a small grey status on
        the next - so a row was two lines tall and still said less than a row
        in the Matters table. It is a TABLE now: name, status and the link
        action side by side on one compact row, so twice as many candidates
        fit in the same panel and each one is read the same way it is read on
        its own page. Same glass surface, same row height, no extra height.
      */}
      <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar-main">
        {isLoading ? (
          <div className="divide-y divide-gray-100 dark:divide-gray-700/60">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="flex items-center justify-between px-5 py-3.5">
                <div className="flex-1 min-w-0 me-3 space-y-2">
                  <div className="h-3.5 w-3/5 bg-gray-200 dark:bg-gray-700 rounded animate-fadePulse" />
                  <div className="h-2.5 w-2/5 bg-surface-sunken/70 rounded animate-fadePulse" />
                </div>
                <div className="h-7 w-14 bg-gray-200 dark:bg-gray-700 rounded animate-fadePulse" />
              </div>
            ))}
          </div>
        ) : resources.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 text-gray-400 dark:text-gray-500">
            {activeTypeConfig?.icon && React.cloneElement(activeTypeConfig.icon, { className: 'w-8 h-8 mb-2 opacity-40' })}
            <p className="text-sm">{searchTerm ? t('No results found') : t('No {{type}} available', { type: activeTypeConfig?.label || '' })}</p>
          </div>
        ) : (
          <table className="w-full table-fixed border-collapse">
            <thead className="sticky top-0 z-10 glass-bar">
              <tr className="text-start">
                <th className="px-5 py-2 text-start text-[11px] font-medium text-fg-muted">{t('Name')}</th>
                <th className="w-36 px-2 py-2 text-start text-[11px] font-medium text-fg-muted whitespace-nowrap">{t('Status')}</th>
                <th className="w-24 px-5 py-2 text-end text-[11px] font-medium text-fg-muted">
                  <span className="sr-only">{t('Link')}</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-hairline-soft">
              {resources.map((resource, index) => {
                const uuid = getResourceUuid(resource);
                const title = getResourceTitle(resource);
                const status = getResourceStatus(resource);
                const isLinked = linkedIds.has(uuid);
                const isLinking = linkingId === uuid;

                return (
                  <tr
                    key={uuid || index}
                    className="h-10 hover:bg-surface-sunken/70 transition-colors"
                  >
                    <td className="px-5 py-1.5 min-w-0">
                      <span className="block truncate text-sm text-fg-strong" title={title}>
                        {title}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap">
                      {status ? (
                        <Badge color={statusColor(status)}>{t(titleCaseStatus(status))}</Badge>
                      ) : (
                        <span className="text-xs text-fg-muted">—</span>
                      )}
                    </td>
                    <td className="px-5 py-1.5 text-end">
                      {isLinked ? (
                        <span className="inline-flex items-center gap-1 text-success-600 dark:text-success-400">
                          <PiCheckCircleLight className="w-4 h-4" />
                          <span className="text-xs font-medium">{t('Linked')}</span>
                        </span>
                      ) : (
                        <Button
                          variant="outline"
                          size="small"
                          onClick={() => handleLink(uuid)}
                          disabled={isLinking}
                        >
                          {isLinking ? (
                            <Spinner size="glyph" className="!h-3 !w-3" />
                          ) : (
                            <>
                              <PiLinkLight className="w-3.5 h-3.5 me-1" />
                              {t('Link')}
                            </>
                          )}
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ProjectResourcesPanel;
